// ============================================================================
// RESTORAN SHABLONLARI (ish modeli to'plamlari)
//
// Platforma admini yangi restoran ochayotganda shulardan birini tanlaydi.
// Shablon — boshlang'ich nuqta, qulf emas: keyin har bir sozlama alohida
// o'zgartiriladi. Shablonlar versiyalanadi: yangi versiya mavjud
// restoranlarni o'z-o'zidan o'zgartirmaydi.
// ============================================================================

import { getModule } from './modules';
import type { ModuleKey } from './modules';
import type { RoleTemplateKey } from './roles';

export type OrderTaker = 'cashier' | 'waiter' | 'both';
export type PaymentTiming = 'before' | 'after' | 'by_order_type';
export type KitchenRouting = 'none' | 'screen' | 'printer' | 'screen_and_printer';
export type OrderType = 'dine_in' | 'takeaway' | 'delivery';

export interface OrderFlow {
  orderTaker: OrderTaker;
  paymentTiming: PaymentTiming;
  kitchenRouting: KitchenRouting;
  orderTypes: readonly OrderType[];
  /** Zal uchun xizmat haqi, foiz. 0 = yo'q */
  serviceChargePercent: number;
}

export interface StationTemplate {
  key: string;
  label: string;
  output: KitchenRouting;
}

export interface RestaurantTemplate {
  key: string;
  version: number;
  label: string;
  description: string;
  modules: readonly ModuleKey[];
  roles: readonly RoleTemplateKey[];
  flow: OrderFlow;
  stations: readonly StationTemplate[];
  /** Ish oqimidan tashqari boshlang'ich sozlamalar (masalan, tungi ish kuni) — Q-055 */
  settings?: Readonly<Record<string, unknown>>;
}

export const RESTAURANT_TEMPLATES = [
  {
    key: 'fast_small',
    version: 1,
    label: 'Kichik fastfood',
    description: '1–2 kassir, oshpazga og‘zaki aytiladi. Avval to‘lov.',
    modules: ['sales', 'inventory', 'hall', 'expenses'],
    roles: ['owner', 'cashier'],
    flow: {
      orderTaker: 'cashier',
      paymentTiming: 'before',
      kitchenRouting: 'none',
      orderTypes: ['takeaway', 'dine_in'],
      serviceChargePercent: 0,
    },
    stations: [],
  },
  {
    key: 'fast_kds',
    version: 1,
    label: 'Fastfood va oshxona ekrani',
    description: 'Oshxona alohida, buyurtma ekranga chiqadi. Avval to‘lov.',
    modules: ['sales', 'inventory', 'hall', 'expenses', 'kds'],
    roles: ['owner', 'manager', 'cashier', 'cook'],
    flow: {
      orderTaker: 'cashier',
      paymentTiming: 'before',
      kitchenRouting: 'screen',
      orderTypes: ['takeaway', 'dine_in'],
      serviceChargePercent: 0,
    },
    stations: [{ key: 'kitchen', label: 'Oshxona', output: 'screen' }],
  },
  {
    key: 'coffee',
    version: 2,
    label: 'Kofe shop',
    description: 'Kassada buyurtma, barista ekranida tayyorlanadi. O‘lchamlar, sut turi va sirop. Avval to‘lov.',
    modules: ['sales', 'inventory', 'expenses', 'kds'],
    roles: ['owner', 'cashier', 'barista'],
    flow: {
      orderTaker: 'cashier',
      paymentTiming: 'before',
      kitchenRouting: 'screen',
      orderTypes: ['takeaway', 'dine_in'],
      serviceChargePercent: 0,
    },
    stations: [{ key: 'bar', label: 'Bar', output: 'screen' }],
  },
  {
    key: 'national',
    version: 1,
    label: 'Milliy restoran / choyxona',
    description: 'Zal va stollar, to‘lov ovqatdan keyin, xizmat haqi. Oshxona va mangal ekrani. Osh, shashlik, choynak choy.',
    modules: ['sales', 'inventory', 'hall', 'expenses', 'kds', 'staff_meals'],
    roles: ['owner', 'manager', 'cashier', 'cook', 'storekeeper'],
    flow: {
      orderTaker: 'cashier',
      paymentTiming: 'after',
      kitchenRouting: 'screen',
      orderTypes: ['dine_in', 'takeaway'],
      serviceChargePercent: 10,
    },
    stations: [
      { key: 'kitchen', label: 'Oshxona', output: 'screen' },
      { key: 'mangal', label: 'Mangal', output: 'screen' },
    ],
    settings: { 'hall.start': true },
  },
  {
    key: 'canteen',
    version: 1,
    label: 'Oshxona (stolovaya)',
    description: 'Tayyor taomlar, kassada tez to‘lov. Birinchi va ikkinchi taomlar, kunlik menyu. Avval to‘lov.',
    modules: ['sales', 'inventory', 'expenses', 'staff_meals'],
    roles: ['owner', 'cashier', 'storekeeper'],
    flow: {
      orderTaker: 'cashier',
      paymentTiming: 'before',
      kitchenRouting: 'none',
      orderTypes: ['dine_in', 'takeaway'],
      serviceChargePercent: 0,
    },
    stations: [],
  },
  {
    key: 'bar',
    version: 1,
    label: 'Bar / lounge',
    description: 'Stolga ochiq hisob, to‘lov oxirida. Ichimliklar ml bo‘yicha, kalyan. Tungi ish kuni (12:00 dan 12:00 gacha).',
    modules: ['sales', 'inventory', 'hall', 'expenses', 'kds'],
    roles: ['owner', 'manager', 'bartender', 'cook'],
    flow: {
      orderTaker: 'cashier',
      paymentTiming: 'after',
      kitchenRouting: 'screen',
      orderTypes: ['dine_in', 'takeaway'],
      serviceChargePercent: 0,
    },
    stations: [{ key: 'kitchen', label: 'Oshxona', output: 'screen' }],
    settings: { 'branch.business_day_start': '12:00', 'hall.start': true },
  },
  {
    key: 'club',
    version: 1,
    label: 'Klub',
    description: 'Bar imkoniyatlari, kirish chiptasi, VIP to‘plamlar va xizmat haqi. Tungi ish kuni (12:00 dan 12:00 gacha).',
    modules: ['sales', 'inventory', 'hall', 'expenses', 'kds'],
    roles: ['owner', 'manager', 'cashier', 'bartender', 'cook'],
    flow: {
      orderTaker: 'cashier',
      paymentTiming: 'after',
      kitchenRouting: 'screen',
      orderTypes: ['dine_in'],
      serviceChargePercent: 10,
    },
    stations: [{ key: 'kitchen', label: 'Oshxona', output: 'screen' }],
    settings: { 'branch.business_day_start': '12:00', 'hall.start': true },
  },
  {
    key: 'cafe_restaurant',
    version: 1,
    label: 'Kafe va restoran',
    description: 'Ofitsiantlar zalda xizmat qiladi. To‘lov ovqatdan keyin.',
    modules: ['sales', 'inventory', 'hall', 'expenses', 'kds', 'waiters', 'kitchen_printers'],
    roles: ['owner', 'manager', 'cashier', 'waiter', 'head_waiter', 'cook', 'storekeeper'],
    flow: {
      orderTaker: 'waiter',
      paymentTiming: 'after',
      kitchenRouting: 'screen_and_printer',
      orderTypes: ['dine_in', 'takeaway'],
      serviceChargePercent: 10,
    },
    stations: [
      { key: 'kitchen', label: 'Oshxona', output: 'screen' },
      { key: 'bar', label: 'Bar', output: 'printer' },
    ],
  },
  {
    key: 'mixed',
    version: 1,
    label: 'Aralash',
    description: 'Zal ofitsiant bilan, olib ketish kassada.',
    modules: ['sales', 'inventory', 'hall', 'expenses', 'kds', 'waiters'],
    roles: ['owner', 'manager', 'cashier', 'waiter', 'head_waiter', 'cook'],
    flow: {
      orderTaker: 'both',
      paymentTiming: 'by_order_type',
      kitchenRouting: 'screen',
      orderTypes: ['dine_in', 'takeaway'],
      serviceChargePercent: 0,
    },
    stations: [{ key: 'kitchen', label: 'Oshxona', output: 'screen' }],
  },
  {
    key: 'blank',
    version: 1,
    label: 'Bo‘sh',
    description: 'Faqat savdo va smena. Qolganini o‘zingiz yoqasiz.',
    modules: ['sales'],
    roles: ['owner'],
    flow: {
      orderTaker: 'cashier',
      paymentTiming: 'before',
      kitchenRouting: 'none',
      orderTypes: ['takeaway'],
      serviceChargePercent: 0,
    },
    stations: [],
  },
] as const satisfies readonly RestaurantTemplate[];

export type RestaurantTemplateKey = (typeof RESTAURANT_TEMPLATES)[number]['key'];

/** Shablonni hozir tanlash mumkinmi: barcha modullari tayyor bo'lishi kerak */
export function isTemplateAvailable(t: RestaurantTemplate): boolean {
  return t.modules.every((m) => getModule(m)?.status === 'available');
}

export function getRestaurantTemplate(key: string): RestaurantTemplate | undefined {
  return (RESTAURANT_TEMPLATES as readonly RestaurantTemplate[]).find((t) => t.key === key);
}

export function availableTemplates(): RestaurantTemplate[] {
  return (RESTAURANT_TEMPLATES as readonly RestaurantTemplate[]).filter(isTemplateAvailable);
}
